import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import {
  HiddenInput,
  Input,
  MaskedInput,
  Select,
  FormPageHeader,
  Textarea,
} from 'components/Shared';
import { FORM_BACK_ACTION, SELECT_OPTIONS } from 'constants/Common';
import type { ClientDocumentType, SelectOption } from 'contracts/Common';
import type { Client } from 'contracts/Clients';
import { useAddressLookup, useCountries, useValidation } from 'hooks';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import {
  FetchClientActions as FetchActions,
  UpdateClientActions as UpdateActions,
} from 'store/ducks/clients';
import { Formatter } from 'utils';
import { UpdateClientValidator } from 'validators/Clients';
import * as S from './styles';

interface Props {
  clientId: string | number;
  onUpdate?: () => void;
}

export const ClientUpdateForm: React.FC<Props> = ({ clientId, onUpdate }) => {
  const dispatch: AppDispatch = useDispatch();
  const formRef = useRef<FormHandles>(null);
  const { handleFormErrors, handleApiErrors } = useValidation();
  const { onZipcodeChange, fetchingAddress } = useAddressLookup(formRef);
  const { countryOptions, loadingCountries, fetchCountries } = useCountries();

  const [selectedType, setSelectedType] = useState<ClientDocumentType>('cnpj');
  const [selectedCountryId, setSelectedCountryId] = useState<number>(1);
  const [logoFile] = useState<File | undefined>();

  const { data: client, loading: fetchingClient } = useSelector(
    (state: RootState) => state.fetchClient
  );

  const { loading: updatingClient, validationErrors } = useSelector(
    (state: RootState) => state.updateClient
  );

  const documentLabel = useMemo(() => {
    if (selectedType === 'cnpj') return 'CNPJ';
    if (selectedType === 'cpf') return 'CPF';
    if (selectedType === 'other') return 'Documento';
  }, [selectedType]);

  const documentMask = useMemo(() => {
    if (selectedType === 'cnpj') return '**.***.***/****-**'; 
    if (selectedType === 'cpf') return '999.999.999-99';
    return [];
  }, [selectedType]);

  const fetchClient = useCallback((): void => {
    dispatch(FetchActions.request(clientId));
  }, [dispatch, clientId]);

  const onClientLoad = useCallback((): void => {
    if (!client) return;
    const { documentType, document, countryId, addressUf } = client;

    const autoSet: Array<keyof Client> = [
      'tradeName',
      'ibge',
      'addressStreet',
      'addressNumber',
      'addressComplement',
      'addressNeighborhood',
      'addressCity',
      'addressState',
      'addressZipcode',
      'addressLatitude',
      'addressLongitude',
      'emails',
      'businessContact',
      'fixedContact',
      'phone',
      'particularities'
    ];

    for (const [field, value] of Object.entries(client)) {
      if (autoSet.includes(field as keyof Client)) {
        formRef.current?.setFieldValue(field, value);
      }
    }

    // manually set fields

    const typeOption = SELECT_OPTIONS.DOCUMENT_TYPES.find(
      (o) => o.value === documentType
    );

    if (typeOption) {
      formRef.current?.setFieldValue('documentType', typeOption);
    }

    setSelectedType(documentType);
    formRef.current?.setFieldValue(
      'document',
      Formatter.document(document, documentType)
    );
    const countryOption = countryOptions.find((o) => o.value === countryId);

    setSelectedCountryId(countryId);
    if (countryOption) {
      formRef.current?.setFieldValue('countryId', countryOption);
    }

    const ufOption = SELECT_OPTIONS.STATES.find((o) => o.value === addressUf);

    if (ufOption) {
      formRef.current?.setFieldValue('addressUf', ufOption);
    }
  }, [client, countryOptions]);

  const onTypeChange = useCallback((option: SelectOption | null): void => {
    if (!option) return;
    setSelectedType(option.value as ClientDocumentType);
  }, []);

  const onCountryChange = useCallback((option: SelectOption | null): void => {
    if (!option) return;
    setSelectedCountryId(option.value as number);
  }, []);

  const onUfChange = useCallback((option: SelectOption | null): void => {
    if (!option) {
      formRef.current?.setFieldValue('addressState', '');
      return;
    }
    formRef.current?.setFieldValue('addressState', option.label);
  }, []);

  const onSuccess = useCallback((): void => {
    formRef?.current?.reset();
    onUpdate && onUpdate();
  }, [onUpdate]);

  const onSubmit = useCallback(
    async (data: any): Promise<void> => {
      try {
        formRef?.current?.setErrors({});
        Object.assign(data, { logoFile });

        const { schema } = new UpdateClientValidator({
          selectedType,
          selectedCountryId,
        });

        const validData = await schema.validate(data, {
          abortEarly: false,
        });

        dispatch(UpdateActions.request(clientId, validData, onSuccess));
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [
      clientId,
      dispatch,
      handleFormErrors,
      logoFile,
      onSuccess,
      selectedCountryId,
      selectedType,
    ]
  );

  const Header = useCallback((): JSX.Element => {
    return (
      <FormPageHeader
        title="Editar cliente"
        icon={<S.CompanyIcon />}
        isLoading={fetchingClient}
        actions={
          <S.LinkButton size="small" to="/configuracoes/clientes">
            <S.ArrowLeftIcon /> Voltar
          </S.LinkButton>
        }
      />
    );
  }, [fetchingClient]);

  useEffect(() => {
    fetchCountries({ excludeBlocked: true });
  }, [fetchCountries]);

  useEffect(() => {
    fetchClient();
  }, [fetchClient]);

  useEffect(() => {
    onClientLoad();
  }, [onClientLoad]);

  useEffect(() => {
    handleApiErrors(validationErrors, formRef);
  }, [handleApiErrors, validationErrors]);

  useEffect(() => {
    return () => {
      dispatch(FetchActions.reset());
      dispatch(UpdateActions.reset());
    };
  }, [dispatch]);

  return (
    <S.MainPanel>
      <Header />
      <S.Content>
        <Form ref={formRef} onSubmit={onSubmit}>
          <HiddenInput name="ibge" readOnly />
          <S.FormRow>
            <Select
              name="documentType"
              label="Tipo de documento"
              options={SELECT_OPTIONS.DOCUMENT_TYPES}
              onChange={onTypeChange}
            />
            <Input
              name="tradeName"
              label={
                selectedType === 'cnpj' ? 'Nome fantasia' : 'Nome completo'
              }
            />
            {!!client?.documentType && (
              <>
                {(['cnpj', 'cpf'].includes(client.documentType as string) && (
                  <MaskedInput
                    name="document"
                    label={documentLabel}
                    mask={documentMask}
                  />
                )) || <Input name="document" label={documentLabel} />}
              </>
            )}
          </S.FormRow>

          <S.FormRow>
            <S.FormRow>
              <Select
                name="countryId"
                label="País"
                options={countryOptions}
                isLoading={loadingCountries}
                onChange={onCountryChange}
              />
              <Input
                name="addressZipcode"
                label="Código postal"
                onChange={onZipcodeChange}
                isLoading={fetchingAddress}
              />
            </S.FormRow>
            <Input name="addressStreet" label="Logradouro" />
            <Input name="addressNumber" label="Número" />
          </S.FormRow>

          <S.FormRow>
            <Input name="addressComplement" label="Complemento" />
            <Input name="addressNeighborhood" label="Bairro" />
            <Input name="addressCity" label="Cidade" />
          </S.FormRow>

          <S.FormRow>
            {(selectedCountryId === 1 && (
              <>
                <HiddenInput name="addressState" readOnly />
                <Select
                  name="addressUf"
                  label="Estado"
                  options={SELECT_OPTIONS.STATES}
                  onChange={onUfChange}
                />
              </>
            )) || <Input name="addressState" label="Estado" />}
            <Input name="addressLatitude" label="Latitude" />
            <Input name="addressLongitude" label="Longitude" />
          </S.FormRow>
          <S.FormRow>
          <Input name="emails" label="E-mails" placeholder='Utilize ; para separar os e-mails' />
          </S.FormRow>

          <S.FormRow>
             <Input name="businessContact" label="Contato comercial" />
             <Input name="fixedContact" label="Contato Fixo"  />
             <Input name="phone" label="Telefone"  />    
          </S.FormRow>
          <S.FormRow>
             <Textarea name="particularities" label="Particularidades" />
          </S.FormRow>

          <S.FormActions>
            <S.LinkButton mood="light" to="/configuracoes/clientes">
              {FORM_BACK_ACTION}
            </S.LinkButton>
            <S.Button type="submit">
              {updatingClient ? <S.ActivityIndicator /> : 'Salvar'}
            </S.Button>
          </S.FormActions>
        </Form>
        {/* <LogoCropper
          label="Logo do cliente"
          helpText="Uma imagem de ao menos 320 x 320px"
          onChange={(file) => setLogoFile(file)}
          preview={client?.logoUrl}
        /> */}
      </S.Content>
    </S.MainPanel>
  );
};
